import type { ValidationError } from '@tanstack/react-form';
import React, { ChangeEventHandler, HTMLInputTypeAttribute } from 'react';
import { classNames } from '@utils/classNames';

export type InputProps = {
  label: string;
  value: string | number;
  errors?: ValidationError[];
  handleBlur?: (e: React.FocusEvent<never>) => void;
  handleChange: ChangeEventHandler<HTMLInputElement>;
  type: HTMLInputTypeAttribute;
  name: string;
  id: string;
  containerClassName?: string;
  inputClassName?: string;
  disabled?: boolean;
  touched?: boolean;
  placeholder?: string;
  testId?: string;
  min?: number;
  max?: number;
};

export default function Input({
  label,
  value,
  errors,
  handleBlur,
  handleChange,
  id,
  name,
  type,
  containerClassName,
  inputClassName,
  disabled,
  touched,
  placeholder,
  testId,
  min,
  max,
}: Readonly<InputProps>) {
  const relevantErrors =
    errors?.filter((error): error is string => !!error) ?? [];

  return (
    <div
      className={classNames(
        'flex flex-col w-full space-y-2 text-dark',
        containerClassName,
      )}
    >
      <label
        htmlFor={id}
        className={classNames('font-bold', disabled ? 'text-grey1' : '')}
      >
        {label}
      </label>
      <input
        id={id}
        type={type}
        name={name}
        className={classNames(
          'text-sm font-light h-10 px-4 py-3 border-solid bg-grey3light rounded-bonusLarge focus:border-2 focus:border-ivarioBlue',
          !disabled && relevantErrors.length && touched
            ? 'border-2 border-error'
            : '',
          (type === 'date' || type === 'time') && !value
            ? 'text-grey1'
            : 'text-dark',
          'disabled:bg-grey3light-50 disabled:text-grey1',
          'placeholder:text-grey1 placeholder:text-sm placeholder:font-light',
          inputClassName,
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
        min={min}
        max={max}
      />
      {!disabled &&
        touched &&
        !!relevantErrors.length &&
        relevantErrors.map((error) => {
          return (
            <p
              className="text-error"
              key={error.toString()}
              data-testid={`${id}-${error}`}
            >
              {error}
            </p>
          );
        })}
    </div>
  );
}
